import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import PageHeader from "../../components/admin/PageHeader";
import LoadingState from "../../components/admin/LoadingState";
import ErrorState from "../../components/admin/ErrorState";
import OnboardingWizard from "../../components/admin/OnboardingWizard";
import useAuth from "../../hooks/useAuth";
import { getBot } from "../../api/chatbotApi";
import { fetchTenantServices, updateTenantService } from "../../api/tenantServiceApi";
import { fetchTenantConfigs } from "../../api/tenantConfigApi";
import { activateTenant, fetchOnboardingStatus, startOnboarding, updateOnboardingStep } from "../../api/onboardingApi";
import { canWriteInTenant, getAccessDeniedMessage } from "../../util/accessControl";

const extractObject = (response) => response?.data || response || null;
const extractList = (response) => response?.data?.items || response?.data || response || [];

const AccessDeniedPage = ({ message, tenantId }) => (
  <div className="min-h-[60vh] flex items-center justify-center">
    <div className="max-w-lg rounded-3xl border border-rose-200 bg-white p-8 text-center shadow-sm">
      <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-rose-50 text-rose-600">
        <svg className="h-7 w-7" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
        </svg>
      </div>
      <h1 className="mt-4 text-xl font-semibold text-slate-950">Access denied</h1>
      <p className="mt-2 text-sm text-slate-600">{message || getAccessDeniedMessage()}</p>
      <div className="mt-6 flex justify-center gap-3">
		<Link to={`/bots/${tenantId}`} className="inline-flex items-center rounded-xl bg-[#096dea] px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-[#0959bb]">
          Back to tenant
        </Link>
        <Link to="/dashboard" className="inline-flex items-center rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-50">
          Dashboard
        </Link>
      </div>
    </div>
  </div>
);

const TenantOnboardingPage = () => {
	const { tenant_id: tenantId } = useParams();
	const { user } = useAuth();

	const isDisabled = !canWriteInTenant(user, tenantId);

	const [loading, setLoading] = useState(true);
	const [error, setError] = useState("");
	const [tenant, setTenant] = useState(null);
	const [services, setServices] = useState([]);
	const [configs, setConfigs] = useState([]);
	const [onboarding, setOnboarding] = useState(null);
	const [steps, setSteps] = useState([]);
	const [actionError, setActionError] = useState("");
	const [actionLoading, setActionLoading] = useState(false);

	const loadOnboarding = async () => {
		setLoading(true);
		setError("");

		try {
			const [tenantResult, onboardingResult, servicesResult, configsResult] = await Promise.allSettled([
				getBot(tenantId),
				fetchOnboardingStatus(tenantId),
				fetchTenantServices(tenantId),
				fetchTenantConfigs(tenantId),
			]);

			if (tenantResult.status === "fulfilled") {
				setTenant(extractObject(tenantResult.value));
			} else {
				throw tenantResult.reason;
			}

			if (onboardingResult.status === "fulfilled") {
				const rec = extractObject(onboardingResult.value);
				setOnboarding(rec);
				setSteps(rec?.steps || rec?.tenant_onboarding_steps || []);
			}

			if (servicesResult.status === "fulfilled") {
				setServices(extractList(servicesResult.value));
			}

			if (configsResult.status === "fulfilled") {
				setConfigs(extractList(configsResult.value));
			}
		} catch (fetchError) {
			setError(fetchError.message || "Failed to load onboarding status.");
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		loadOnboarding();
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [tenantId]);

	const withAction = async (task) => {
		setActionError("");
		setActionLoading(true);
		try {
			await task();
			await loadOnboarding();
		} catch (taskError) {
			setActionError(taskError.message || "Action failed.");
		} finally {
			setActionLoading(false);
		}
	};

	const handleToggleService = async (service) => {
		setActionError("");
		setActionLoading(true);
		try {
			await updateTenantService(tenantId, service.service_key, {
				status: service.status === "enabled" ? "disabled" : "enabled",
				config_json: service.config_json || {},
			});
			await loadOnboarding();
		} catch (err) {
			const status = err?.response?.status ?? err?.status;
			setActionError(
				status === 403
					? "Permission denied: you do not have access to modify this service."
					: err.message || "Failed to update service."
			);
		} finally {
			setActionLoading(false);
		}
	};

	const handleStart = () => withAction(() => startOnboarding(tenantId));
	const handleUpdateStep = (stepKey, dataJson = {}) => withAction(() => updateOnboardingStep(tenantId, stepKey, {
		completed: true,
		data_json: dataJson,
	}));
	const handleActivate = () => withAction(() => activateTenant(tenantId));

	if (loading) return <LoadingState label="Loading onboarding..." />;
	if (error) {
		const is403 = error.includes("403") || error.toLowerCase().includes("forbidden") || error.toLowerCase().includes("access denied");
		if (is403) return <AccessDeniedPage message={error} tenantId={tenantId} />;
		return <ErrorState description={error} onRetry={loadOnboarding} />;
	}
	if (!tenant) return <ErrorState description="Tenant not found." onRetry={loadOnboarding} />;

	return (
		<div className="space-y-6">
			<PageHeader
				eyebrow="Platform admin"
				title={`Onboarding · ${tenant.name}`}
				description="Manage the onboarding state and required setup steps for this tenant."
				actionLabel="Back to tenant"
				actionTo={`/bots/${tenantId}`}
			/>

			{actionError ? (
				<div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
					{actionError}
				</div>
			) : null}

			<OnboardingWizard
				onboarding={onboarding}
				steps={steps}
				tenant={tenant}
				tenantId={tenantId}
				services={services}
				configs={configs}
				onStart={handleStart}
				onStepUpdate={handleUpdateStep}
				onActivate={handleActivate}
				onToggleService={handleToggleService}
				isDisabled={isDisabled}
				loading={actionLoading}
			/>
		</div>
	);
};

export default TenantOnboardingPage;
